import Phaser from 'phaser';
import { isCalmMode } from '../audio/CalmMode.js';

/**
 * Globally gate every camera shake/flash behind Calm Mode in a single place.
 *
 * There are ~38 `cameras.main.shake(...)` / `.flash(...)` call-sites scattered
 * across the scenes and effect classes. Rather than touch each one, we wrap the
 * Camera prototype methods once at boot: when Calm Mode is on they become
 * no-ops (returning `this` so any chaining stays valid); otherwise they call
 * through to the originals. This also covers any future shake/flash for free.
 *
 * Shakes/flashes are purely cosmetic — no gameplay reads them — so suppressing
 * them is safe. Non-camera full-screen effects (the edge-pulse strobe, widget
 * pulses) are gated separately at their own call-sites.
 */

let installed = false;

export function installCalmCameraGuard(): void {
  if (installed) return;
  installed = true;

  const proto = Phaser.Cameras.Scene2D.Camera.prototype;

  const originalShake = proto.shake;
  const originalFlash = proto.flash;

  proto.shake = function (this: Phaser.Cameras.Scene2D.Camera, ...args: unknown[]) {
    if (isCalmMode()) return this;
    return (originalShake as (...a: unknown[]) => Phaser.Cameras.Scene2D.Camera).apply(this, args);
  } as typeof proto.shake;

  proto.flash = function (this: Phaser.Cameras.Scene2D.Camera, ...args: unknown[]) {
    if (isCalmMode()) return this;
    return (originalFlash as (...a: unknown[]) => Phaser.Cameras.Scene2D.Camera).apply(this, args);
  } as typeof proto.flash;
}
